import { Component, Input, forwardRef, Injector, effect, computed, signal, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormControl, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors, NgControl, FormsModule } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-coverage-amount-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NgxMaskDirective, DecimalPipe],
  templateUrl: './coverage-amount-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoverageAmountInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CoverageAmountInputComponent),
      multi: true
    }
  ]
})
export class CoverageAmountInputComponent implements ControlValueAccessor, Validator, OnInit {

  @Input() currency: string = 'RD$';

  get currencySymbol(): string {
    const cur = (this.currency || '').trim().toUpperCase();
    return cur === 'USD' ? 'US$' : (cur === 'DOP' ? 'RD$' : this.currency);
  }
  @Input() min?: number;
  @Input() max?: number;
  @Input() label?: string;
  @Input() placeholder: string = '0.00';
  @Input() inputId: string = `coverage-input-${Math.random().toString(36).substr(2, 9)}`;
  @Input() readonly: boolean = false;

  value: number | string | null = null;
  disabled: boolean = false;

  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  private lastEmittedValue: number | null | undefined = undefined;

  constructor() { }

  ngOnInit(): void { }

  writeValue(value: any): void {
    this.value = value;
    // When value is written from outside, we trust it. 
    // We update lastEmittedValue so we don't echo it back in onModelChange.
    this.lastEmittedValue = value !== null && value !== undefined && value !== '' ? Number(value) : null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onModelChange(val: any): void {
    // val comes from ngx-mask (raw value if dropSpecialCharacters is true, which is default)
    // We ensure it's a number for keys validation and propagation
    const num = val !== null && val !== '' ? Number(val) : null;

    if (num !== this.lastEmittedValue) {
      this.lastEmittedValue = num;
      this.onChange(num);
    }
  }

  onBlur(): void {
    this.onTouched();
    // Optional: clamp on blur if desired, but user feedback via error is often better
    // if (this.value !== null) {
    //   if (this.min !== undefined && this.value < this.min) {
    //     this.value = this.min;
    //     this.onChange(this.value);
    //   } else if (this.max !== undefined && this.value > this.max) {
    //     this.value = this.max;
    //     this.onChange(this.value);
    //   }
    // }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.readonly) return null;
    if (this.value === null || this.value === undefined || this.value === '') {
      // Assuming required is handled by parent if needed, or we can add @Input() required
      return null;
    }

    const val = Number(this.value);
    const errors: ValidationErrors = {};
    let hasError = false;

    if (this.min !== undefined && val < this.min) {
      errors['min'] = { min: this.min, actual: val };
      hasError = true;
    }

    if (this.max !== undefined && val > this.max) {
      errors['max'] = { max: this.max, actual: val };
      hasError = true;
    }

    return hasError ? errors : null;
  }

  get showMinError(): boolean {
    if (this.disabled || this.readonly) return false;
    const val = Number(this.value);
    return this.min !== undefined && this.value !== null && this.value !== '' && !isNaN(val) && val < this.min;
  }

  get showMaxError(): boolean {
    if (this.disabled || this.readonly) return false;
    const val = Number(this.value);
    return this.max !== undefined && this.value !== null && this.value !== '' && !isNaN(val) && val > this.max;
  }
}
